import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';
import {
  calculateFromM2,
  calculateFromQuantity,
  formatM2Display,
  getLineItemCoverageProperty,
  shouldShowM2Input,
} from '@theme/coverage-calculations';
import { parseIntOrDefault } from '@theme/utilities';

/**
 * Cart-only coverage quantity editor.
 *
 * The visible input accepts m², while the hidden input keeps Shopify's
 * `updates[]` value as the sellable cart quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} m2Input
 * @property {HTMLInputElement} quantityInput
 *
 * @extends {Component<Refs>}
 */
class CartCoverageQuantityComponent extends Component {
  requiredRefs = ['m2Input', 'quantityInput'];

  /** @type {string | null} */
  #m2FocusValue = null;

  connectedCallback() {
    super.connectedCallback();
    this.#syncFromQuantity();
  }

  /**
   * @returns {string}
   */
  get #distributionType() {
    return this.dataset.distributionType || '';
  }

  /**
   * @returns {number}
   */
  get #mPerPack() {
    return parseFloat(this.dataset.mPerPack || '1') || 1;
  }

  /**
   * @returns {number}
   */
  get #distributionUnitMultiple() {
    return parseFloat(this.dataset.distributionUnitMultiple || '1') || 1;
  }

  /**
   * @returns {string}
   */
  get #primarySaleUnit() {
    return this.dataset.primarySaleUnit || '';
  }

  /**
   * @returns {boolean}
   */
  get #calculatorRequired() {
    return this.dataset.calculatorRequired !== 'false';
  }

  /**
   * @returns {number}
   */
  get #tilesPerM() {
    return parseFloat(this.dataset.tilesPerM || '0') || 0;
  }

  /**
   * @returns {number}
   */
  get #m2Step() {
    return parseFloat(this.dataset.m2Step || '1') || 1;
  }

  /**
   * @returns {import('@theme/coverage-calculations').PricingMode}
   */
  get #pricingMode() {
    const mode = this.dataset.pricingMode || 'per_item';
    if (mode === 'per_m2_direct' || mode === 'per_m2_converted') {
      return mode;
    }

    return 'per_item';
  }

  /**
   * @returns {number}
   */
  get #m2DecimalPlaces() {
    const decimals = parseInt(this.dataset.m2Decimals || '', 10);
    return Number.isNaN(decimals) ? 2 : Math.max(0, decimals);
  }

  /**
   * @returns {{min: number, max: number|null, step: number, value: number}}
   */
  getCurrentValues() {
    const { quantityInput } = this.refs;

    return {
      min: parseIntOrDefault(quantityInput.min, 1),
      max: parseIntOrDefault(quantityInput.max, null),
      step: parseIntOrDefault(quantityInput.step, 1),
      value: parseIntOrDefault(quantityInput.value, 0),
    };
  }

  /**
   * @param {number} m2
   * @returns {string}
   */
  #formatM2(m2) {
    return formatM2Display(m2, this.#m2DecimalPlaces);
  }

  /**
   * @param {number} quantity
   * @returns {import('@theme/coverage-calculations').CoverageValues}
   */
  #calculateFromQuantity(quantity) {
    return calculateFromQuantity(
      this.#distributionType,
      quantity,
      this.#mPerPack,
      this.#distributionUnitMultiple,
      this.#primarySaleUnit,
      this.#calculatorRequired,
      this.#tilesPerM
    );
  }

  /**
   * @param {number} m2
   * @returns {import('@theme/coverage-calculations').CoverageValues}
   */
  #calculateFromM2(m2) {
    return calculateFromM2(
      this.#distributionType,
      m2,
      this.#mPerPack,
      this.#distributionUnitMultiple,
      this.#primarySaleUnit,
      this.#calculatorRequired,
      this.#tilesPerM
    );
  }

  /**
   * Cart lines use absolute max quantities, so clamp converted quantity before dispatching.
   *
   * @param {import('@theme/coverage-calculations').CoverageValues} values
   * @returns {import('@theme/coverage-calculations').CoverageValues}
   */
  #applyQuantityBounds(values) {
    const { min, max } = this.getCurrentValues();
    const quantity = Math.min(max ?? Infinity, Math.max(min, values.quantity));

    if (quantity === values.quantity) {
      return values;
    }

    return this.#calculateFromQuantity(quantity);
  }

  /**
   * @param {import('@theme/coverage-calculations').CoverageValues} values
   * @param {boolean} [dispatchUpdate]
   * @param {boolean} [applyBounds]
   */
  #applyValues(values, dispatchUpdate = true, applyBounds = true) {
    const boundedValues = applyBounds ? this.#applyQuantityBounds(values) : values;
    const { quantityInput, m2Input } = this.refs;

    quantityInput.value = String(boundedValues.quantity);
    m2Input.value = this.#formatM2(boundedValues.displayM2);

    if (dispatchUpdate) {
      const lineItemProperties = getLineItemCoverageProperty(
        boundedValues,
        this.#pricingMode,
        this.#m2DecimalPlaces
      );

      this.dispatchEvent(
        new QuantitySelectorUpdateEvent(
          boundedValues.quantity,
          Number(quantityInput.dataset.cartLine) || undefined,
          lineItemProperties ?? undefined
        )
      );
    }
  }

  /**
   * @param {Event} event
   * @param {number} stepMultiplier
   */
  #stepM2(event, stepMultiplier) {
    event.preventDefault();

    const { m2Input } = this.refs;
    if (m2Input.disabled) return;

    const minM2 = parseFloat(m2Input.min || '0') || 0;
    const value = parseFloat(m2Input.value);
    const currentM2 = Number.isNaN(value) ? minM2 : value;
    const nextM2 = Math.max(minM2, currentM2 + this.#m2Step * stepMultiplier);

    this.#applyValues(this.#calculateFromM2(nextM2));
    this.#m2FocusValue = m2Input.value;
  }

  #syncFromQuantity() {
    if (
      !shouldShowM2Input({
        distributionType: this.#distributionType,
        primarySaleUnit: this.#primarySaleUnit,
        calculatorRequired: this.#calculatorRequired,
        isSample: this.dataset.isSample === 'true',
      })
    ) {
      return;
    }

    this.#applyValues(this.#calculateFromQuantity(this.getCurrentValues().value), false, false);
  }

  /**
   * @param {string | number} [quantity]
   */
  resetQuantity(quantity = this.refs.quantityInput.defaultValue) {
    this.refs.quantityInput.value = String(quantity);
    this.#syncFromQuantity();
  }

  /**
   * @param {FocusEvent} event
   */
  selectM2Input(event) {
    if (!(event.target instanceof HTMLInputElement)) return;
    this.#m2FocusValue = event.target.value;
    event.target.select();
  }

  /**
   * @param {Event} event
   */
  setM2FromInput(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    event.preventDefault();

    if (this.#m2FocusValue !== null && event.target.value === this.#m2FocusValue) {
      this.#m2FocusValue = null;
      return;
    }

    this.#m2FocusValue = null;
    const raw = parseFloat(event.target.value);
    const m2 = Number.isNaN(raw) ? this.#m2Step : raw;

    this.#applyValues(this.#calculateFromM2(m2));
  }

  /**
   * @param {KeyboardEvent} event
   */
  onM2KeyDown(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    if (event.key === 'ArrowUp') {
      this.#stepM2(event, 1);
    } else if (event.key === 'ArrowDown') {
      this.#stepM2(event, -1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      this.setM2FromInput(event);

      // The committed value is now the focus baseline, so the later blur does
      // not dispatch the same cart update a second time.
      this.#m2FocusValue = event.target.value;
    }
  }
}

if (!customElements.get('cart-coverage-quantity-component')) {
  customElements.define('cart-coverage-quantity-component', CartCoverageQuantityComponent);
}
